import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PageWrapper from "../components/PageWrapper";
import { useAuth } from "../context/AuthContext";
import { labs } from "../data/labs";
import LabTerminal from "../sections/lab/LabTerminal";
import { subscribeLabHealth } from "../lib/firebase";

const Wrap = styled.section`
  background: var(--nd-black);
  min-height: 100vh;
  padding: 128px 24px 96px;
  color: var(--nd-text-primary);
  font-family: 'Space Grotesk', sans-serif;
`;
const Inner = styled.div` max-width: 1000px; margin: 0 auto; `;
const Kicker = styled.p`
  font-family: 'Space Mono', monospace; font-size: 11px; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--nd-text-disabled); margin: 0 0 12px;
`;
const Title = styled.h1`
  font-size: 40px; font-weight: 500; color: var(--nd-text-display);
  letter-spacing: -0.02em; margin: 0 0 24px;
`;
const Grid = styled.div` display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; @media (max-width: 720px){ grid-template-columns: 1fr; } `;
const LabCard = styled.button`
  text-align: left; background: transparent; color: inherit; cursor: pointer;
  border: 1px solid ${({ active }) => (active ? "var(--nd-text-display)" : "var(--nd-border)")};
  border-radius: 8px; padding: 16px; font-family: inherit;
  &:hover { border-color: var(--nd-border-visible); }
`;
const CardTitle = styled.div` color: var(--nd-text-display); font-size: 15px; margin-bottom: 6px; `;
const CardMeta = styled.div` font-family: 'Space Mono', monospace; font-size: 11px; color: var(--nd-text-secondary); `;
const Notice = styled.p` font-family: 'Space Mono', monospace; font-size: 12px; color: var(--nd-text-disabled); `;
const HealthBanner = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-accent);
  border: 1px solid var(--nd-accent);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
  letter-spacing: 0.04em;
`;
const DisabledCard = styled.button`
  text-align: left; background: transparent; color: inherit; cursor: not-allowed;
  border: 1px solid var(--nd-border); border-radius: 8px; padding: 16px; font-family: inherit;
  opacity: 0.35;
`;

const canAccessLab = (role, tags) => role === "admin" || (Array.isArray(tags) && tags.includes("junior"));

const labHealthBannerText = (h) => {
  if (!h || h.ok !== false) return null;
  const parts = [];
  if (!h.k8s) parts.push("Kubernetes");
  if (!h.vm) parts.push("Docker VM");
  const what = parts.length ? parts.join(" + ") : (h.reason || "infrastructure");
  return `Live labs are temporarily unavailable — ${what} is down. Try again shortly.`;
};

const LabPage = () => {
  const { user, role, tags } = useAuth();
  const [activeLab, setActiveLab] = useState(null);
  const [labHealth, setLabHealth] = useState(null);

  useEffect(() => {
    return subscribeLabHealth(setLabHealth);
  }, []);

  const healthBanner = labHealthBannerText(labHealth);
  const labsDown = labHealth != null && labHealth.ok === false;

  if (!user) {
    return (
      <PageWrapper darkTheme>
        <Wrap><Inner><Kicker>Dioscuro · Labs</Kicker><Title>Live labs</Title>
          <Notice>Sign in to launch a lab.</Notice></Inner></Wrap>
      </PageWrapper>
    );
  }
  if (!canAccessLab(role, tags)) {
    return (
      <PageWrapper darkTheme>
        <Wrap><Inner><Kicker>Dioscuro · Labs</Kicker><Title>Live labs</Title>
          <Notice>Labs are available to trainees (junior) and admins.</Notice></Inner></Wrap>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper darkTheme>
      <Wrap>
        <Inner>
          <Kicker>Dioscuro · Labs</Kicker>
          <Title>Live labs</Title>
          {healthBanner && <HealthBanner>{healthBanner}</HealthBanner>}
          {!activeLab && (
            <Grid>
              {labs.map((l) => labsDown ? (
                <DisabledCard key={l.id} type="button" disabled aria-disabled="true">
                  <CardTitle>{l.title}</CardTitle>
                  <CardMeta>{l.category} · {l.difficulty} · {l.time}</CardMeta>
                </DisabledCard>
              ) : (
                <LabCard key={l.id} type="button" onClick={() => setActiveLab(l.id)}>
                  <CardTitle>{l.title}</CardTitle>
                  <CardMeta>{l.category} · {l.difficulty} · {l.time}</CardMeta>
                </LabCard>
              ))}
            </Grid>
          )}
          {activeLab && (
            <>
              <LabCard as="button" type="button" onClick={() => setActiveLab(null)} style={{ marginBottom: 12 }}>
                <CardMeta>&larr; back to labs</CardMeta>
              </LabCard>
              <LabTerminal labId={activeLab} />
            </>
          )}
        </Inner>
      </Wrap>
    </PageWrapper>
  );
};

export default LabPage;
